import { ChevronRight, Repeat2 } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { Button, EmptyState, Pressable, RelativeTime, RowStatus, Tooltip, cn, formatClock, formatInt, pipelineItemRowStatus, plural } from '@orgus/ui';
import { commitTitle, failureSummary } from './present.ts';
import { ResultColumns, ResultRow } from './ResultRow.tsx';
import type { RowStatusProps } from '@orgus/ui';
import { chainSummary, pipelineChains, resubmitted } from './chains.ts';
import type { Chain, ChainOutcome } from './chains.ts';

/** Chains shown before "Show all". */
const PREVIEW = 8;

/** A chain's outcome as a row status: landed is green here, the news a landing queue reports. */
const OUTCOME: Record<ChainOutcome, Pick<RowStatusProps, 'tone' | 'icon' | 'label'>> = {
  landed: { tone: 'success', icon: 'done', label: 'Landed' },
  'in-flight': { tone: 'working', icon: 'working', label: 'In flight' },
  failed: { tone: 'danger', icon: 'failed', label: 'Rejected' },
  cancelled: { tone: 'neutral', icon: 'stopped', label: 'Withdrawn' },
};

/** The attempts of one chain, oldest first, each one click from its log. */
export function AttemptChain({ chain, onOpen, activeId }: { chain: Chain; onOpen?: (i: PipelineItem) => void; activeId?: string | null }) {
  return (
    <ol className="flex flex-wrap items-center gap-1" aria-label={`${plural(chain.attempts.length, 'attempt')}, oldest first`}>
      {chain.attempts.map((a, i) => {
        const st = pipelineItemRowStatus(a.state);
        const label = a.state === 'failed' ? 'Rejected' : a.state === 'passed' ? 'Landed' : st.label;
        const at = a.endedAt ?? a.startedAt;
        const body = (
          <>
            <span className="text-fg-subtle num">{i + 1}</span>
            <RowStatus {...st} label={label} className="text-caption" />
            {at != null && <span className="text-fg-subtle num">{formatClock(at)}</span>}
          </>
        );
        const cls = cn('flex h-control-xs items-center gap-2 rounded-control border px-2 text-caption', a.id === activeId ? 'border-accent-line bg-accent-soft' : 'border-border bg-surface');
        return (
          <li key={a.id} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="size-4 text-fg-faint" aria-hidden />}
            <Tooltip content={a.detail ? `${label}: ${a.detail}` : `${label}${a.by ? ` from ${a.by}` : ''}`}>
              {onOpen ? (
                <Pressable onClick={() => onOpen(a)} className={cn(cls, 'w-auto hover:border-border-strong hover:bg-hover')}>
                  {body}
                </Pressable>
              ) : (
                <span tabIndex={0} className={cls}>
                  {body}
                </span>
              )}
            </Tooltip>
          </li>
        );
      })}
    </ol>
  );
}

/** Work submitted more than once: each piece of work as one row (its newest attempt opens). */
export function ResubmittedList({ pipeline: p, onOpen, query = '' }: { pipeline: Pipeline; onOpen: (i: PipelineItem) => void; query?: string }) {
  const every = useMemo(() => resubmitted(pipelineChains(p)), [p]);
  const q = query.trim().toLowerCase();
  const chains = useMemo(() => (q ? every.filter((c) => c.title.toLowerCase().includes(q)) : every), [every, q]);
  const [all, setAll] = useState(false);
  const shown = all ? chains : chains.slice(0, PREVIEW);
  if (!chains.length) {
    if (q) return <p className="px-4 py-8 text-center text-ui text-fg-subtle">No change matches "{query.trim()}"</p>;
    return (
      <EmptyState
        icon={<Repeat2 />}
        title="Nothing was submitted twice"
        description="Work rejected and submitted again shows here as one row."
      />
    );
  }
  return (
    <div className="flex flex-col px-1 pb-1">
      <ResultColumns first="Attempts" last="Last" />
      <ol data-list="" className="flex flex-col">
        {shown.map((c) => {
          const lastFail = [...c.attempts].reverse().find((a) => a.state === 'failed');
          const why = lastFail && c.outcome !== 'landed' ? failureSummary(lastFail, p).reason : null;
          const meta = `${chainSummary(c, p)}${why ? `, last: ${why}` : ''}`;
          const t = commitTitle(c.title);
          return (
            <ResultRow
              key={c.key}
              status={OUTCOME[c.outcome]}
              title={t.subject}
              fullTitle={c.title}
              type={t.type}
              detail={meta}
              first={formatInt(c.attempts.length)}
              firstTitle={plural(c.attempts.length, 'attempt')}
              last={<RelativeTime ts={c.lastAt} />}
              onOpen={() => onOpen(c.latest)}
            />
          );
        })}
        {chains.length > shown.length && (
          <li className="flex h-row items-center gap-3 px-row-x">
            <span className="w-4 shrink-0" aria-hidden />
            <Button variant="ghost" size="sm" className="-ml-2 text-fg-subtle" onClick={() => setAll(true)}>
              Show all {formatInt(chains.length)}
            </Button>
          </li>
        )}
      </ol>
    </div>
  );
}
